import React from "react";
import {
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { buildServiceSchema, buildPersonSchema, type Crumb } from "../utils/structuredData";
import { useBookAssessment } from "../hooks/useBookAssessment";

interface AboutSection {
  title: string;
  description: string;
  bullets: string[];
}

interface TeamMember {
  name: string;
  role: string;
  credential: string;
}

// Leadership team. Listed alongside the advisory board (not in a separate,
// smaller section) so the two read as one collective leadership group.
const executiveTeam: TeamMember[] = [
  { name: "Anthony Caruso", role: "Chief Executive Officer", credential: "Leads deployments for education and financial-services. Specializes in Google Workspace, AWS, & Oracle Cloud" },
  { name: "Thomas Gammer", role: "Chief Technology Officer", credential: "Leads deployments for healthcare and legal. Specializes in Microsoft 365 and custom systems" },
  { name: "Azeez Shaik", role: "Chief Product Officer", credential: "Leads deployments for government and distribution. Specializes in on-prem solutions and legacy system integration" },
];

// Advisory board: industry experts who've worked inside the kinds of regulated
// organizations Contextral builds for.
const advisoryBoard: TeamMember[] = [
  { name: "Gitanjali Singh", role: "Regulations & Compliance Advisor", credential: "Director of Regulatory Affairs @ AstraZeneca" },
  { name: "Michael Sachs", role: "Strategic Financial Advisor", credential: "Value Management and Customer Success @ Salesforce, AWS, and Oracle" },
  { name: "Mike Brzezinski", role: "Strategic Hardware Advisor", credential: "Director of Cloud Sales @ AMD" },
  { name: "Otima Bhanot", role: "Strategic Healthcare Advisor", credential: "Global Healthcare Leader & Strategic Leader @ EvolvX Strategy" },
  { name: "Tyreek Rolon", role: "Strategic Municipal Advisor", credential: "Director of Emergency Management @ Newark City Hall" },
];

const initials = (name: string) =>
  name
    .split(" ")
    .map((part) => part[0])
    .join("");

const About: React.FC = () => {
  const location = useLocation();
  const bookAssessment = useBookAssessment();

  const heading = "AI Automation That Works the Way You Do";
  const intro = "We're a services team that builds a secure AI automation layer between the systems you already run and where your data lives — diagnosing the real problem, designing around your environment, and staying involved long after launch.";
  const sections: AboutSection[] = [
    {
      title: "Organizations Were Sold Tools and Left to Struggle",
      description: "Too many teams bought AI software, watched it get implemented poorly, and were left on their own. We do the opposite.",
      bullets: [
        "We diagnose the real problem before recommending anything.",
        "We design around the systems and infrastructure you already have.",
        "We own the outcome, not just the install.",
      ],
    },
    {
      title: "Secure Automation Should Fit Your Environment",
      description: "Your automation should respect your data and compliance requirements, and connect the MSP-managed platforms, ERPs, and line-of-business tools your team already uses — not replace them.",
      bullets: [
        "Security and access control come first, by design.",
        "We integrate Microsoft 365, Google Workspace, EMRs, ERPs, and legacy or custom apps through secure connectors.",
        "Your documents and data flow into storage you own — document management, cloud storage, or your own databases.",
      ],
    },
    {
      title: "We Work Alongside Your Team",
      description: "We assess how your work actually flows, design automation around it, deploy in phases, and stay on as your long-term partner.",
      bullets: [
        "Discovery and assessment of your real workflows.",
        "Tailored design that builds on what you have.",
        "Phased deployment with little to no staff training.",
        "Continued support as your needs evolve.",
      ],
    },
  ];
  const closingHeading = "A Partner for the Long Run";
  const closingBody = [
    "AI is becoming core to how organizations operate.",
    "But it only delivers when it's applied responsibly and fits how you already work.",
    "That's what we do: secure automation, built around your environment, with our team alongside you.",
  ];

  const seoTitle = `${heading} | Contextral`;
  const seoDescription = intro;
  const serviceSchema = buildServiceSchema(location.pathname, heading, intro);
  const teamSchemas = [...executiveTeam, ...advisoryBoard].map((person) =>
    buildPersonSchema(person.name, person.role, person.credential),
  );

  const crumbs: Crumb[] = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
  ];

  return (
    <div className="relative pt-6">
      <Seo title={seoTitle} description={seoDescription} jsonLd={[serviceSchema, ...teamSchemas]} />
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_14%_18%,rgba(21,116,186,0.08),transparent_36%),radial-gradient(circle_at_86%_72%,rgba(110,100,183,0.1),transparent_42%)]"
        aria-hidden="true"
      />

      <section>
        <Breadcrumbs items={crumbs} />
        <div className="text-center mb-10 sm:mb-14">
          <h1 className="font-bold text-text-primary mb-4 text-3xl sm:text-4xl lg:text-5xl leading-tight">
            {heading}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
            {intro}
          </p>
        </div>
      </section>

      <section>
        <div className="relative overflow-hidden rounded-2xl border border-borderSubtle bg-surface shadow-sm">
          <div
            className="pointer-events-none absolute left-0 top-0 h-full w-px bg-gradient-to-b from-primary/50 via-accentPurple/40 to-accentPink/30"
            aria-hidden="true"
          />

          {sections.map((section, index) => (
            <div
              key={section.title}
              className={`px-5 sm:px-8 lg:px-10 py-10 sm:py-12 border-b border-borderSubtle/90 ${index % 2 === 1 ? "bg-gradient-to-r from-primary/5 via-transparent to-accentPurple/5" : ""}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
                <div className={`lg:col-span-7 order-2 lg:order-${index % 2 === 0 ? "1" : "2"}`}>
                  <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-4">
                    {section.title}
                  </h2>
                  <p className="text-text-secondary leading-relaxed mb-5">
                    {section.description}
                  </p>
                  <ul className="space-y-2.5 text-text-secondary">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`lg:col-span-5 order-1 lg:order-${index % 2 === 0 ? "2" : "1"}`}>
                  <div className="relative overflow-hidden rounded-2xl border border-borderSubtle bg-surfaceSoft">
                    <img
                      src={`/images/about/aboutSection${index + 1}.webp`}
                      alt={section.title}
                      className="h-64 sm:h-72 w-full object-cover"
                      loading={index === 0 ? "eager" : "lazy"}
                      fetchPriority={index === 0 ? "high" : "auto"}
                      decoding="async"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="px-5 sm:px-8 lg:px-10 py-10 sm:py-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
              <div className="lg:col-span-7 order-2 lg:order-2">
                <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-4">
                  {closingHeading}
                </h2>
                <div className="max-w-5xl space-y-4 text-text-secondary leading-relaxed">
                  {closingBody.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-5 order-1 lg:order-1">
                <div className="relative overflow-hidden rounded-2xl border border-borderSubtle bg-surfaceSoft">
                  <img
                    src="/images/about/aboutSection7.webp"
                    alt="Two Contextral professionals walking together through a modern office"
                    className="h-64 sm:h-72 w-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Leadership & Advisory Board
          </h2>
          <p className="text-base sm:text-lg text-text-secondary max-w-4xl mx-auto leading-relaxed">
            Our team is backed by industry experts who understand the problems you're trying to
            solve — from regulatory compliance and healthcare to municipal operations, cloud
            infrastructure, and enterprise technology.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {[...advisoryBoard, ...executiveTeam].map((person) => (
            <div
              key={person.name}
              className="rounded-2xl border border-borderSubtle bg-surface p-6 text-center shadow-sm transition-all duration-300 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10"
            >
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-primary/20 bg-gradient-to-br from-primary/15 to-accentPurple/20 text-lg font-bold text-primary">
                {initials(person.name)}
              </div>
              <h3 className="text-base font-bold text-text-primary">{person.name}</h3>
              <p className="mt-1 text-sm font-medium italic text-primary">{person.role}</p>
              <p className="mt-2 text-sm text-text-secondary leading-relaxed">{person.credential}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="relative overflow-hidden bg-gradient-to-r from-primary via-primaryHover to-accentPurple rounded-2xl p-6 sm:p-8 md:p-12 text-center shadow-xl shadow-primary/25">
          <div className="pointer-events-none absolute inset-0 bg-blueprint opacity-30" aria-hidden="true" />
          <h2 className="relative text-2xl sm:text-3xl md:text-4xl font-bold text-text-inverse mb-3 sm:mb-4">
            If this approach resonates, we would be happy to talk.
          </h2>
          <p className="relative text-surfaceSoft text-base sm:text-lg md:text-xl max-w-3xl mx-auto mb-7">
            We can walk through your environment, your data requirements, and what a
            private, local AI system could look like for your team.
          </p>
          <div className="relative flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <NavLink
              to="/contact"
              className="group w-full sm:w-auto inline-flex items-center justify-center bg-surface text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:scale-105 transform transition-all duration-300 shadow-lg"
            >
              Contact Our Team
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </NavLink>
            <button
              type="button"
              onClick={() => bookAssessment()}
              className="group w-full sm:w-auto inline-flex items-center justify-center border border-white/60 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white/10 transition-all duration-300"
            >
              Book a Free Automation Assessment
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
